import { SameDayTag } from '@/components/blocks/SameDayTag';
import { DeliveryOption } from '@/components/content/ProductDetails/parts/DeliveryMethodSelector/parts/DeliveryOption';
import { DeliveryStockCounter } from '@/components/content/ProductDetails/parts/DeliveryMethodSelector/parts/DeliveryStockCounter';
import { deliveryMethodSelectorSX } from '@/components/content/ProductDetails/parts/DeliveryMethodSelector/styles/deliveryMethodSelector';
import { isSelectableStatus } from '@/components/content/ProductDetails/parts/DeliveryMethodSelector/utils/isSelectableStatus';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { FULFILLMENT_METHOD } from '@/data/constants/inventory';
import { PdpHomeDeliveryOption } from '@/data/Content/PdpHomeDeliveryOption';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { ContentContext } from '@/data/context/content';
import { useLocalization } from '@/data/Localization';
import { Box, Skeleton, Typography } from '@mui/material';
import { FC, useContext } from 'react';
import {
	MATERIAL_SYMBOL_ICONS,
	OpenSansFontWeights,
} from 'shared/custom/constants/commonConstants';

type Props = {
	delivery: PdpHomeDeliveryOption;
	alignStockBottom?: boolean;
};

type AvailabilityLabels = ReturnType<typeof useLocalization<'HDMProductDetail'>>['Availability'];

/** 159073 AC8-AC10: three reasons apply to home delivery -- pickup-only product
 * (`not-available`), zip-restricted (`blocked-zip`, also drives the message
 * block below the selector, see `DeliveryMethodSelector`), and everything else
 * disabled (out of stock, missing lead time, etc.) reads simply "Agotado". */
const getUnavailableMessage = (
	status: PdpHomeDeliveryOption['status'],
	Availability: AvailabilityLabels
): string => {
	if (status === 'not-available') return Availability.labels.homeDeliveryNotAvailable.t();
	if (status === 'blocked-zip') return Availability.labels.homeDeliveryBlockedZip.t();
	return Availability.labels.empty.t();
};

/** AC9: "Entrega estimada hoy/mañana/el {dd Mmm}" -- reuses the same prefix +
 * today/tomorrow wording as the PLP's `HomeDeliveryDateLabel`, not duplicated.
 * Only rendered for `with-date`/`same-day` (163024); `available-no-date` (AC10) shows just the counter. */
const getDateLine = (
	delivery: PdpHomeDeliveryOption,
	Availability: AvailabilityLabels
): { prefix: string; highlight: string } | undefined => {
	if (delivery.status !== 'with-date' && delivery.status !== 'same-day') return undefined;
	if (delivery.descriptor === 'date') {
		return {
			prefix: Availability.labels.homeDeliveryDatePrefixWithDate.t(),
			highlight: delivery.formatted,
		};
	}
	return {
		prefix: Availability.labels.homeDeliveryDatePrefix.t(),
		highlight:
			delivery.descriptor === 'today'
				? Availability.labels.deliveryPickupToday.t()
				: Availability.labels.deliveryPickupTomorrow.t(),
	};
};

export const HomeDeliveryOption: FC<Props> = ({ delivery, alignStockBottom }) => {
	const { isDeliverySelected, selectBox } = useContext(ContentContext) as ReturnType<
		typeof useProductDetails
	>;
	const { Availability, DeliveryMethodSelector: labels } = useLocalization('HDMProductDetail');
	const disabled = !isSelectableStatus(delivery.status);

	const unavailableMessage = disabled
		? getUnavailableMessage(delivery.status, Availability)
		: undefined;

	const dateLine = getDateLine(delivery, Availability);

	if (delivery.status === 'pending') {
		return <Skeleton variant="text" sx={deliveryMethodSelectorSX.skeletonOption} />;
	}

	// 163146 AC5/AC10: the express variant is exactly the `same-day` state, which the
	// EDD resolution only yields with store stock, before EDD_SAMEDAY_CUTOFF_TIME and
	// under every other home delivery rule (AC13); otherwise the regular button.
	const isExpress = delivery.status === 'same-day';

	return (
		<DeliveryOption
			id={isExpress ? 'deliveryExpressButton' : 'deliveryOptionHomeDelivery'}
			className={isExpress ? 'delivery-selector__button--express' : undefined}
			label={labels.HomeDelivery.t()}
			icon={
				isExpress ? (
					<SameDayTag />
				) : (
					<DynamicIcon
						name={MATERIAL_SYMBOL_ICONS.localShipping}
						sx={deliveryMethodSelectorSX.icon(disabled)}
						variant={isDeliverySelected && !disabled ? 'filled' : 'rounded'}
						size={24}
					/>
				)
			}
			selected={isDeliverySelected}
			disabled={disabled}
			onSelect={selectBox(FULFILLMENT_METHOD.DELIVERY)}
		>
			{unavailableMessage ? (
				<Typography
					variant="bodyBaseline"
					color="grey.200"
					component="div"
					className="delivery-selector__unavailable-message"
					sx={deliveryMethodSelectorSX.spacing}
				>
					{unavailableMessage}
				</Typography>
			) : dateLine ? (
				<Box
					component="div"
					className="delivery-selector__date"
					sx={deliveryMethodSelectorSX.spacing}
				>
					<Typography variant="bodyBaseline" color="grey.500">
						{dateLine.prefix}
					</Typography>
					<Typography
						variant="bodyBaseline"
						color="grey.500"
						fontWeight={OpenSansFontWeights.semibold}
					>
						{dateLine.highlight}
					</Typography>
				</Box>
			) : null}
			{!disabled && delivery.availableQuantity !== undefined ? (
				<DeliveryStockCounter
					availableQuantity={delivery.availableQuantity}
					alignBottom={alignStockBottom}
				/>
			) : null}
		</DeliveryOption>
	);
};
